import React, { useMemo, useState, useEffect } from 'react';
import { BubbleOverlay, BubbleTable, BubbleButton, BubbleBadge } from './BubbleUI';
import { BareSailingDay, BareSailing } from '@go-shared/types/sailing';
import { TemplateFragmentType } from '@go-shared/types/templateFragment';
import { Template } from '@go-shared/types/template';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from './BubbleUI/BubbleTable';
import { camelToSentenceCase } from '@go-shared/lib/string';
import { getLocaleDateStringUTC } from '@go-shared/lib/time';
import { safeIdCompare } from '@go-shared/lib/utils';
import { getSailingsByTemplateFragment } from '@go-client/lib/api/itinerary';
import { BubbleBadgePropsBase } from './BubbleUI/BubbleBadge';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';

interface TemplateViewFormTableItem {
  key: string;
  name: string;
  sailing: string;
  date: string;
  fragments: TemplateFragmentType[];
  _sailing: BareSailing;
  _sailingDay: BareSailingDay;
}

interface TemplateAssociationPropsBase {
  template: Template;
  onSailingDayNameClick?: (sailing: BareSailing, sailingDay: BareSailingDay) => void;
}

type TemplateAssociationProps = React.HTMLAttributes<HTMLDivElement> & TemplateAssociationPropsBase;

export default function TemplateAssociations(props: TemplateAssociationProps) {
  const { template, onSailingDayNameClick, ...rest } = props;
  const { activeProperty: property } = useActiveProperty();
  const { serverDate } = usePublicServerInfo();

  const [loading, setLoading] = useState(false);
  const [sailings, setSailings] = useState<BareSailing[]>([]);

  useEffect(() => {
    fetchAssociatedSailings().catch(console.error);
  }, [props.template]);

  async function fetchAssociatedSailings() {
    setLoading(true);
    try {
      const sails = await getSailingsByTemplateFragment(props.template._id, serverDate.iso);
      setSailings(sails);
    } finally {
      setLoading(false);
    }
  }

  const tableItems: TemplateViewFormTableItem[] = useMemo(() => {
    const items: TemplateViewFormTableItem[] = [];

    sailings.forEach(sailing => {
      sailing.days.forEach(day => {
        const { templateFragments } = day;
        const fragments: TemplateFragmentType[] = [];

        if (
          templateFragments &&
          templateFragments.events &&
          safeIdCompare(templateFragments.events, template._id)
        ) {
          fragments.push('events');
        }

        if (
          templateFragments &&
          templateFragments.venueData &&
          safeIdCompare(templateFragments.venueData, template._id)
        ) {
          fragments.push('venueData');
        }

        if (
          templateFragments &&
          templateFragments.feed &&
          safeIdCompare(templateFragments.feed, template._id)
        ) {
          fragments.push('feed');
        }

        if (fragments.length < 1) return;

        items.push({
          key: day._id,
          sailing: sailing.name,
          name: `Day ${day.name}`,
          date: getLocaleDateStringUTC(day.date),
          fragments,
          _sailing: sailing,
          _sailingDay: day,
        });
      });
    });

    return items;
  }, [sailings]);

  const sailingLabel = getPropertyConfiguration(property.type, 'sailingLabel');

  const tableFields: Array<BubbleTableFields<TemplateViewFormTableItem>> = [
    {
      key: 'sailing',
      label: sailingLabel,
    },
    {
      key: 'name',
    },
    {
      key: 'date',
      behavior: BubbleTableFieldBehavior.NoWrap,
    },
    {
      key: 'fragments',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<TemplateViewFormTableItem>> = [
    {
      key: 'name',
      render: item => (
        <BubbleButton
          variant="navy"
          inverse
          className="p-0-i stretched-link"
          icon={['fass', 'chart-tree-map']}
          onClick={() => {
            if (props.onSailingDayNameClick) {
              props.onSailingDayNameClick(item._sailing, item._sailingDay);
            }
          }}
        >
          <span className="underline">{item.name}</span>
        </BubbleButton>
      ),
    },
    {
      key: 'fragments',
      render: item => (
        <>
          {item.fragments.map(frg => {
            const variantDict: { [key in TemplateFragmentType]: BubbleBadgePropsBase['variant'] } =
              {
                events: 'lime',
                venueData: 'banana',
                content: 'ocean',
                feed: 'grape',
              };

            return (
              <BubbleBadge key={frg} className="mr-1 mt-1" variant={variantDict[frg]}>
                {camelToSentenceCase(frg)}
              </BubbleBadge>
            );
          })}
        </>
      ),
    },
  ];

  return (
    <div {...rest}>
      <BubbleOverlay loading={loading}>
        <BubbleTable
          responsive
          headVariant="navy"
          striped
          fields={tableFields}
          items={tableItems}
          templates={tableTemplates}
        />
      </BubbleOverlay>
    </div>
  );
}
